import React, { useState, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Trash2, FileText, Code, AlertCircle } from 'lucide-react';
import '../../../../scriptGeneration/styles/CreateProjectModal.css';
import logger from '../../../../../shared/services/logging/logger';
import { PROJECT_CONSTANTS, validateFileSize } from '../../../config/constants';

const CreateProjectModal = ({ isOpen, onClose, onSubmit, existingProjects, userData, prefillData }) => {

  const [formData, setFormData] = useState({
    projectName: '',
    description: '',
    version: '',
    priority: 'medium'
  });

  const [attachment, setAttachment] = useState(null);
  const [fileSizeWarning, setFileSizeWarning] = useState('');
  const [error, setError] = useState('');
  const [projectNameError, setProjectNameError] = useState('');
  const [versionError, setVersionError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Function to capitalize entire string (all uppercase)
  const capitalizeWords = (str) => {
    return str.replace(/\s/g, '_').toUpperCase();
  };

  const validateProjectName = (name) => {
    if (!name.trim()) {
      setProjectNameError('');
      return true;
    }

    if (name.length < PROJECT_CONSTANTS.MIN_NAME_LENGTH) {
      setProjectNameError(PROJECT_CONSTANTS.ERRORS.NAME_TOO_SHORT(PROJECT_CONSTANTS.MIN_NAME_LENGTH));
      return false;
    }

    if (name.length > PROJECT_CONSTANTS.MAX_NAME_LENGTH) {
      setProjectNameError(PROJECT_CONSTANTS.ERRORS.NAME_TOO_LONG(PROJECT_CONSTANTS.MAX_NAME_LENGTH));
      return false;
    }

    if (!/^[A-Z0-9_]+$/.test(name)) {
      setProjectNameError('Project name can only contain letters, numbers, and underscores');
      return false;
    }

    setProjectNameError('');
    return true;
  };

  // Function to validate version format
  const validateVersion = (version) => {
    if (!version.trim()) {
      setVersionError('Version is required');
      return false;
    }

    // Allow: numbers, dots, hyphens, underscores, and the letter 'v'
    // Examples: 1.0.0, v1.0.0, 2.1.3-beta, 1.0_alpha
    const versionRegex = /^[vV]?[\d]+(\.[\d]+)*(-[\w]+)*(\_[\w]+)*$/;

    if (!versionRegex.test(version.trim())) {
      setVersionError('Invalid version format (e.g., 1.0.0, v2.1.3, 1.0-beta)');
      return false;
    }

    setVersionError('');
    return true;
  };

  const validateDescription = (description) => {
    if (!description.trim()) {
      setDescriptionError('Description is required');
      return false;
    }

    setDescriptionError('');
    return true;
  };

  // Function to check if project name exists
  const checkProjectNameExists = (name) => {
    if (!name.trim()) {
      setProjectNameError('');
      return false;
    }
   
    if (!existingProjects || existingProjects.length === 0) {
      setProjectNameError('');
      return false;
    }
    
    const normalizedName = name.toLowerCase().trim();
    
    const exists = existingProjects.some(project => {
      const projectName = project.name || project.projectName || '';
      const normalizedProjectName = projectName.toLowerCase().trim();
      return normalizedProjectName === normalizedName;
    });
    
    if (exists) {
      setProjectNameError('Project name already exists');
      return true;
    } else {
      setProjectNameError('');
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Auto-capitalize project name (FULL UPPERCASE)
    if (name === 'projectName') {
      processedValue = capitalizeWords(value);
      // Check for existing project names as user types
      setTimeout(() => {
        checkProjectNameExists(processedValue);
        validateProjectName(processedValue);
      }, 100);
    }

    // Validate version as user types
    if (name === 'version') {
      setTimeout(() => validateVersion(value), 100);
    }

    // Validate description as user types
    if (name === 'description') {
      setTimeout(() => validateDescription(value), 100);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  // Clear errors and prefill form when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setProjectNameError('');
      setVersionError('');
      setDescriptionError('');

      // Prefill form fields if returning from a failed generation
      if (prefillData) {
        setFormData({
          projectName: prefillData.projectName || '',
          description: prefillData.description || '',
          version: prefillData.version || '',
          priority: prefillData.priority || 'medium'
        });
      }
    }
  }, [isOpen, prefillData]);

  // Helper function to determine MIME type from file extension if not provided
  const getMimeType = (fileName, browserMimeType) => {
    // If browser provided a valid MIME type, use it
    if (browserMimeType && browserMimeType !== 'application/octet-stream') {
      return browserMimeType;
    }

    // Otherwise, determine from file extension
    const ext = fileName.toLowerCase().split('.').pop();
    const mimeTypes = {
      // API Specifications
      'yaml': 'application/x-yaml',
      'yml': 'application/x-yaml',
      'json': 'application/json',
      
      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain',
      'md': 'text/markdown',
      
      // Data files
      'csv': 'text/csv',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      
      // Presentations
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      
      // Testing
      'feature': 'text/plain',
      
      // Images
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg'
    };

    return mimeTypes[ext] || 'application/octet-stream';
  };

  // ✅ FIXED: Store raw File object with correct MIME type
  const validateAndProcessFile = async (file) => {
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.pdf', '.docx', '.doc', '.txt', '.md', '.csv', '.xlsx', '.xls', '.pptx', '.ppt', '.feature', '.yaml', '.yml', '.json', '.png', '.jpeg', '.jpg'];
    const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isAllowed) {
      setError('Please upload an accepted file type (PDF, DOCX, TXT, MD, CSV, XLSX, PPTX, YAML, JSON, PNG, JPG, JPEG etc.)');
      return;
    }

    // Check file size — warn but don't block
    const fileSizeIssue = validateFileSize(file.size, file.name);
    if (fileSizeIssue) {
      setFileSizeWarning('This file exceeds the maximum size limit and cannot be used. Please upload a smaller file.');
    } else {
      setFileSizeWarning('');
    }

    setIsProcessingFile(true);
    setError('');

    try {
      // Determine correct MIME type
      const mimeType = getMimeType(file.name, file.type);
      
      // Create a new File object with correct MIME type if needed
      let processedFile = file;
      if (file.type !== mimeType) {
        processedFile = new File([file], file.name, { type: mimeType });
      }

      logger.debug('Storing file with MIME type', {
        name: processedFile.name,
        type: processedFile.type,
        size: processedFile.size
      });

      setAttachment(processedFile);
    } catch (error) {
      logger.error('Failed to process file', {
        error: error.message,
        fileName: file?.name,
        fileSize: file?.size
      });
      setError('Error processing file. Please try again.');
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    await validateAndProcessFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set dragging to false if we're leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isProcessingFile || attachment) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await validateAndProcessFile(files[0]);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setFileSizeWarning('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setError('');

    let isValid = true;

    if (!formData.projectName.trim()) {
      setProjectNameError('Project name is required');
      isValid = false;
    } else if (checkProjectNameExists(formData.projectName)) {
      isValid = false;
    } else if (!validateProjectName(formData.projectName)) {
      isValid = false;
    }

    // Validate description
    if (!validateDescription(formData.description)) {
      isValid = false;
    }

    // Validate version
    if (!validateVersion(formData.version)) {
      isValid = false;
    }

    if (!attachment) {
      setError('Please attach an API specification or documentation file');
      isValid = false;
    } else if (fileSizeWarning) {
      setError('Please upload a smaller file within the size limits before continuing.');
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    // ✅ FIXED: Pass raw File object (now with correct MIME type)
    onSubmit({
      ...formData,
      attachment // This is now a File object with correct type property
    });
  };

  const getFileIcon = (fileName) => {
    if (fileName.toLowerCase().includes('.yaml') || fileName.toLowerCase().includes('.yml')) {
      return <Code size={16} className="file-icon yaml" />;
    }
    return <FileText size={16} className="file-icon doc" />;
  };

  if (!isOpen) return null;

  return (
    <div className="project-modal-overlay">
      <div className="project-modal" onClick={(e) => e.stopPropagation()}>
        <div className="project-modal-header">
          <h2>Create New Project</h2>
          <button
            className="project-modal-close"
            onClick={onClose}
            disabled={isProcessingFile}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <p className="project-modal-subtitle">Configure your project</p>

        <div className="project-modal-form">
          <div className="project-form-grid">
            <div className="project-form-group">
              <label htmlFor="projectName">Project Name *</label>
              <input
                type="text"
                id="projectName"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                placeholder="e.g., CUSTOMER API"
                required
                maxLength={50}
                minLength={3}
                className={projectNameError ? 'error' : ''}
              />
              {projectNameError && (
                <div className="field-error-message">
                  <AlertCircle size={14} />
                  <span>{projectNameError}</span>
                </div>
              )}
            </div>

            <div className="project-form-group">
              <label htmlFor="version">Version *</label>
              <input
                type="text"
                id="version"
                name="version"
                value={formData.version}
                onChange={handleInputChange}
                placeholder="1.0.0"
                required
                className={versionError ? 'error' : ''}
              />
              {versionError && (
                <div className="field-error-message">
                  <AlertCircle size={14} />
                  <span>{versionError}</span>
                </div>
              )}
            </div>

            <div className="project-form-group full-width">
              <label>Priority Level</label>
              <div className="project-priority-group">
                {['low', 'medium', 'high'].map(level => (
                  <button
                    key={level}
                    type="button"
                    className={`project-priority-btn ${formData.priority === level ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, priority: level }))}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            <div className="project-form-group full-width">
              <label htmlFor="description">
                Description *
                <span className="char-count">
                  {formData.description.length}/{PROJECT_CONSTANTS.MAX_DESCRIPTION_LENGTH}
                </span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Outline the core objectives and architectural scope..."
                rows={3}
                maxLength={PROJECT_CONSTANTS.MAX_DESCRIPTION_LENGTH}
                required
                className={descriptionError ? 'error' : ''}
              />
              {descriptionError && (
                <div className="field-error-message">
                  <AlertCircle size={14} />
                  <span>{descriptionError}</span>
                </div>
              )}
            </div>
          </div>

          <div className="project-attachment-section">
            <div className="project-attachment-header">
              <h3>API Specification</h3>
              <p>
                Upload your API specification file to ensure accurate and project-specific configuration generation.
              </p>
            </div>

            <div className="project-file-upload">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".yaml,.yml,.json,.pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls,.pptx,.ppt,.feature,.png,.jpeg,.jpg"
                className="project-file-input"
                disabled={isProcessingFile}
              />
              
              {!attachment ? (
                <div 
                  ref={dropZoneRef}
                  className={`project-file-dropzone ${isDragging ? 'dragging' : ''}`}
                  onClick={() => !isProcessingFile && fileInputRef.current?.click()}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12" y2="12"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                  <span>
                    {isProcessingFile
                      ? 'Processing file...'
                      : isDragging
                        ? 'Drop file here'
                        : <><strong>Drag & drop</strong> API files or <strong style={{textDecoration: 'underline'}}>browse</strong></>}
                  </span>
                  <small>JSON, YAML OR SWAGGER FORMATS SUPPORTED</small>
                </div>
              ) : (
                <>
                  <div className="project-file-selected">
                    <div className="project-file-info">
                      {getFileIcon(attachment.name)}
                      <span>{attachment.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeAttachment}
                      className="project-file-remove"
                      disabled={isProcessingFile}
                      aria-label="Remove file"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  {fileSizeWarning && (
                    <div className="project-file-size-warning">
                      <AlertCircle size={14} />
                      <span>{fileSizeWarning}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="project-file-limits-warning">
              <AlertCircle size={14} />
              <div>
                <strong>File size limits:</strong>
                <ul>
                  <li>Documents (PDF, TXT, CSV, DOCX, etc.): max 4.5 MB</li>
                  <li>Images (JPEG, PNG): max 3.75 MB</li>
                </ul>
              </div>
            </div>

            <div className="project-file-requirements">
              <div className="project-requirements-header">
                <h4>Accepted Files:</h4>
                <div className="project-help-tooltip">
                  <span className="project-help-trigger">
                    What makes a good file? 
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 7V11M8 5V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </span>
                  
                  <div className="project-help-content">
                    <div className="project-help-section">
                      <h5>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        Valid Inputs for Best Results:
                      </h5>
                      <ul>
                        <li>OpenAPI/Swagger YAML with <code>paths:</code> and <code>openapi:</code> fields</li>
                        <li>CSV/Excel with columns: method, endpoint/path</li>
                        <li>JSON array with endpoint objects (HTTP method + path)</li>
                        <li>Structured API documentation with clear endpoint definitions</li>
                      </ul>
                    </div>

                    <div className="project-help-section">
                      <h5>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        Not Recommended:
                      </h5>
                      <ul>
                        <li>Requirements documents (BRD, FRS) without endpoint details</li>
                        <li>Test configuration files</li>
                        <li>Plain text API descriptions without structure</li>
                        <li>Gherkin feature files (use for test scenarios, not API specs)</li>
                        <li>YAML files missing <code>paths:</code> section</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <ul>
                <li><strong>Specifications:</strong> YAML, YML, JSON</li>
                <li><strong>Documents:</strong> PDF, DOCX, DOC, TXT, MD</li>
                <li><strong>Data:</strong> CSV, XLSX, XLS</li>
                <li><strong>Presentations:</strong> PPTX, PPT</li>
                <li><strong>Testing:</strong> .feature (Gherkin)</li>
                <li><strong>Images:</strong> PNG, JPEG, JPG</li>
              </ul>
            </div>
          </div>

        </div>

        {error && (
          <div className="project-error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="project-modal-actions">
          <button
            type="button"
            className="project-btn-cancel"
            onClick={onClose}
            disabled={isProcessingFile}
          >
            Cancel
          </button>
          <button
            type="button"
            className="project-btn-create"
            onClick={handleSubmit}
            disabled={isProcessingFile || !!projectNameError || !!versionError || !!descriptionError || !!fileSizeWarning}
          >
            {isProcessingFile ? 'Processing...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

CreateProjectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  existingProjects: PropTypes.arrayOf(PropTypes.shape({
    projectName: PropTypes.string,
    name: PropTypes.string
  })),
  userData: PropTypes.shape({
    email: PropTypes.string,
    userId: PropTypes.string,
    organizationId: PropTypes.string
  }),
  prefillData: PropTypes.shape({
    projectName: PropTypes.string,
    description: PropTypes.string,
    version: PropTypes.string,
    priority: PropTypes.string
  })
};

CreateProjectModal.defaultProps = {
  existingProjects: [],
  userData: null,
  prefillData: null
};

const arePropsEqual = (prevProps, nextProps) => {
  // Check primitives
  if (prevProps.isOpen !== nextProps.isOpen) return false;

  // Check functions (reference equality)
  if (prevProps.onClose !== nextProps.onClose) return false;
  if (prevProps.onSubmit !== nextProps.onSubmit) return false;

  // Check arrays - compare length
  if (prevProps.existingProjects?.length !== nextProps.existingProjects?.length) return false;

  // Check userData object
  if (prevProps.userData?.email !== nextProps.userData?.email) return false;
  if (prevProps.userData?.userId !== nextProps.userData?.userId) return false;
  if (prevProps.userData?.organizationId !== nextProps.userData?.organizationId) return false;

  return true;
};

CreateProjectModal.displayName = 'CreateProjectModal';

export default memo(CreateProjectModal, arePropsEqual);